import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Pencil, Minus, Plus, Trash2 } from 'lucide-react';
import { Segmented } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import {
  type ReminderProfile,
  type ReminderProfileRule,
  reminderRules,
} from '@/services/scheduling/use-prebook-appointment-profile';
import type { AppointmentFormValues } from '@/types/appointment-form';

interface ReminderEditorViewProps {
  /** Closes the view; when `commit` is true, writes the edited profile
   * back to the form. False = revert (Flutter Cancel). */
  onClose: (commit?: boolean) => void;
}

const TYPE_OPTIONS = [
  { value: 'Email', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'InApp', label: 'In-App' },
];

const TIME_OPTIONS = ['Hour', 'Day', 'Week', 'Month'];
const WHEN_OPTIONS = ['Before', 'After'];

/** Backend ships the rule's type field as either Flutter `time/whenToSend/number`
 * or the older `unit/beforeAfter/duration`. Read both, write Flutter shape. */
function ruleType(r: ReminderProfileRule): string {
  return r.type ?? '';
}
function ruleNumber(r: ReminderProfileRule): string {
  if (r.number !== undefined) return r.number;
  if (r.duration !== undefined) return String(r.duration);
  return '';
}
function ruleTime(r: ReminderProfileRule): string {
  return r.time ?? r.unit ?? '';
}
function ruleWhen(r: ReminderProfileRule): string {
  return r.whenToSend ?? r.beforeAfter ?? '';
}

/**
 * ReminderEditorView — full panel-content replacement.
 * Mirrors Flutter `edit_reminder_profile_widget.dart` (top fields)
 * + `edit_reminder_widgets/edit_reminder_profile_type_widget.dart`
 * (per-rule inline editor — TYPE / NUMBER / TIME / WHEN chips with
 * Done/Cancel/trash).
 *
 *   - Top bar: Back / "General" (literal title — Flutter line 82) / Save
 *   - PROFILE NAME text field (mandatory, max 100)
 *   - CUSTOM NOTE textarea (optional, max 100)
 *   - Per-rule rows; pencil expands into the inline sub-editor that
 *     mirrors `EditReminderProfileTypeWidget` 1:1.
 *
 * The top-bar Save commits both the profile fields AND any rule
 * changes back to the form. Per-rule Done commits the rule edit
 * locally only — the user must still tap top-bar Save to persist.
 */
export function ReminderEditorView({ onClose }: ReminderEditorViewProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const stored = watch('reminderProfile');

  // Snapshot on mount; later form changes don't clobber the editor.
  const initial = useMemo<ReminderProfile>(
    () => (stored ? structuredClone(stored as ReminderProfile) : { reminderProfileType: [] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [profileName, setProfileName] = useState<string>(initial.profileName ?? '');
  const [customNote, setCustomNote] = useState<string>(initial.customNote ?? '');
  const [rules, setRules] = useState<ReminderProfileRule[]>(
    initial.reminderProfileType ?? [],
  );

  // Per-rule edit state — index of the rule currently in edit mode (or null)
  // and a working-copy draft that the row's Done commits back into `rules`.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<ReminderProfileRule | null>(null);

  const beginEdit = (i: number) => {
    setEditingIndex(i);
    setDraft(structuredClone(rules[i]));
  };
  const cancelEdit = () => {
    setEditingIndex(null);
    setDraft(null);
  };
  const commitEdit = () => {
    if (editingIndex === null || !draft) return;
    setRules((prev) => prev.map((r, i) => (i === editingIndex ? draft : r)));
    cancelEdit();
  };
  const deleteRule = (i: number) => {
    setRules((prev) => prev.filter((_, idx) => idx !== i));
    if (editingIndex === i) cancelEdit();
  };

  const dirty =
    profileName.trim() !== (initial.profileName ?? '').trim() ||
    customNote !== (initial.customNote ?? '') ||
    JSON.stringify(rules) !== JSON.stringify(initial.reminderProfileType ?? []);
  // Disallow top-bar Save while a per-rule editor is open — user must
  // Done/Cancel first (matches Flutter's gating).
  const saveEnabled = dirty && profileName.trim().length > 0 && editingIndex === null;

  const onSave = () => {
    if (!saveEnabled) return;
    const next: ReminderProfile = {
      ...initial,
      profileName: profileName.trim(),
      customNote,
      reminderProfileType: rules,
    };
    setValue('reminderProfile', next, { shouldValidate: true });
    setValue('reminderProfileName', next.profileName ?? null);
    setValue('reminderProfileRules', reminderRules(next));
    onClose(true);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default flex-shrink-0">
        <button
          onClick={() => onClose(false)}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="text-base font-bold text-text-primary">General</h2>
        <button
          type="button"
          onClick={onSave}
          disabled={!saveEnabled}
          className={cn(
            'text-sm font-medium',
            saveEnabled
              ? 'text-brand-primary hover:text-interactive-hover'
              : 'text-text-hint cursor-not-allowed',
          )}
        >
          Save
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4">
          <div className="rounded-lg border border-border-default bg-surface-card p-4 space-y-4">
            {/* Profile name */}
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Profile name <span className="text-status-error">*</span>
              </label>
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value.slice(0, 100))}
                maxLength={100}
                placeholder="Enter profile name"
                className="w-full h-10 px-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
            </div>

            {/* Custom note */}
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Custom note
              </label>
              <textarea
                value={customNote}
                onChange={(e) => setCustomNote(e.target.value.slice(0, 100))}
                maxLength={100}
                placeholder="Add Custom Note to patient (Optional)"
                rows={3}
                className="w-full px-3 py-2 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary resize-none"
              />
              <p className="text-xs text-text-hint text-right mt-1">
                {customNote.length}/100
              </p>
            </div>

            {/* Rule rows — read-only summary or inline sub-editor */}
            <div className="rounded-lg border border-border-default overflow-hidden">
              {rules.length === 0 ? (
                <p className="text-sm text-text-muted px-4 py-3">
                  No reminder rules configured.
                </p>
              ) : (
                rules.map((rule, i) => {
                  const isLast = i === rules.length - 1;
                  if (editingIndex === i && draft) {
                    return (
                      <RuleEditor
                        key={`edit-${i}`}
                        draft={draft}
                        rules={rules}
                        rowIndex={i}
                        onChange={setDraft}
                        onDone={commitEdit}
                        onCancel={cancelEdit}
                        onDelete={() => deleteRule(i)}
                        isLast={isLast}
                      />
                    );
                  }
                  return (
                    <RuleRow
                      key={`row-${i}`}
                      rule={rule}
                      isLast={isLast}
                      onEdit={() => beginEdit(i)}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface RuleRowProps {
  rule: ReminderProfileRule;
  isLast: boolean;
  onEdit: () => void;
}

function RuleRow({ rule, isLast, onEdit }: RuleRowProps) {
  const number = ruleNumber(rule);
  const time = ruleTime(rule).toLowerCase();
  const when = (ruleWhen(rule) || 'before').toLowerCase().trim();
  const subline = `${number} ${time} ${when}`.replace(/\s+/g, ' ').trim();
  const typeLabel = ruleType(rule) === 'InApp' ? 'In-App' : ruleType(rule);

  return (
    <div
      className={cn(
        'flex items-center justify-between px-4 py-3',
        isLast ? '' : 'border-b border-border-subtle',
      )}
    >
      <div className="min-w-0">
        <p className="text-sm font-bold text-text-primary truncate">
          {typeLabel || '—'}
        </p>
        <p className="text-sm text-text-muted leading-snug">{subline || '—'}</p>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="p-1 hover:bg-grey-100 rounded flex-shrink-0"
        aria-label="Edit rule"
      >
        <Pencil className="h-4 w-4 text-brand-primary" />
      </button>
    </div>
  );
}

interface RuleEditorProps {
  draft: ReminderProfileRule;
  rules: ReminderProfileRule[];
  rowIndex: number;
  onChange: (next: ReminderProfileRule) => void;
  onDone: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isLast: boolean;
}

/**
 * RuleEditor — inline expansion of a single rule row.
 * Mirrors Flutter `edit_reminder_profile_type_widget.dart`:
 *   - TYPE chips (other rules' types are disabled — Flutter excludes them)
 *   - NUMBER stepper (min 1; matches `_decrement` guard at line 161)
 *   - TIME chips (Hour / Day / Week / Month)
 *   - WHEN chips (Before / After)
 *   - Done / Cancel / trash row
 *
 * Done is gated on TYPE + NUMBER (>0) + TIME + WHEN all being set.
 */
function RuleEditor({
  draft,
  rules,
  rowIndex,
  onChange,
  onDone,
  onCancel,
  onDelete,
  isLast,
}: RuleEditorProps) {
  // Types in use by OTHER rules — those chips become disabled here.
  const usedByOthers = new Set(
    rules
      .filter((_, i) => i !== rowIndex)
      .map((r) => ruleType(r))
      .filter(Boolean),
  );

  const number = ruleNumber(draft) || '0';
  const numberInt = Number.parseInt(number, 10) || 0;
  const time = ruleTime(draft);
  const when = ruleWhen(draft);
  const type = ruleType(draft);

  const setNumber = (n: number) => {
    onChange({ ...draft, number: String(Math.max(1, n)), duration: undefined });
  };

  const doneEnabled = !!type && numberInt >= 1 && !!time && !!when;

  return (
    <div
      className={cn(
        'px-4 py-4 space-y-4',
        isLast ? '' : 'border-b border-border-subtle',
      )}
    >
      {/* TYPE */}
      <div>
        <label className="block text-xs font-medium text-text-muted mb-2">
          Type <span className="text-status-error">*</span>
        </label>
        {/* Per-option `disabled` preserved: a type already chosen by
            another rule is locked out (unless it's this rule's current
            selection). Segmented won't fire onChange for a disabled
            option, so the `!disabled` guard is implicit. */}
        <Segmented
          options={TYPE_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: opt.value !== type && usedByOthers.has(opt.value),
          }))}
          value={type || undefined}
          onChange={(v) => onChange({ ...draft, type: v })}
          aria-label="Reminder type"
        />
      </div>

      {/* NUMBER */}
      <div>
        <label className="block text-xs font-medium text-text-muted mb-2">
          Number
        </label>
        <div className="inline-flex items-center gap-3">
          <input
            type="number"
            min={1}
            value={number}
            onChange={(e) => setNumber(Number.parseInt(e.target.value, 10) || 1)}
            className="w-12 h-8 text-center rounded-md bg-grey-100 text-sm text-text-primary tabular-nums focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary"
          />
          <button
            type="button"
            onClick={() => setNumber(numberInt - 1)}
            disabled={numberInt <= 1}
            className={cn(
              'h-8 w-8 flex items-center justify-center rounded-full',
              numberInt > 1
                ? 'text-brand-primary hover:bg-blue-100'
                : 'text-text-hint cursor-not-allowed',
            )}
            aria-label="Decrement"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="text-text-hint">|</span>
          <button
            type="button"
            onClick={() => setNumber(numberInt + 1)}
            className="h-8 w-8 flex items-center justify-center rounded-full text-brand-primary hover:bg-blue-100"
            aria-label="Increment"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* TIME */}
      <div>
        <label className="block text-xs font-medium text-text-muted mb-2">
          Time <span className="text-status-error">*</span>
        </label>
        <Segmented
          options={TIME_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
          value={time || undefined}
          onChange={(v) => onChange({ ...draft, time: v, unit: undefined })}
          aria-label="Reminder time unit"
        />
      </div>

      {/* WHEN */}
      <div>
        <label className="block text-xs font-medium text-text-muted mb-2">
          When <span className="text-status-error">*</span>
        </label>
        <Segmented
          options={WHEN_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
          value={when || undefined}
          onChange={(v) =>
            onChange({ ...draft, whenToSend: v, beforeAfter: undefined })
          }
          aria-label="Reminder timing"
        />
      </div>

      {/* Done / Cancel / Delete */}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={onDone}
          disabled={!doneEnabled}
          className={cn(
            'h-8 px-4 rounded-md text-sm font-semibold',
            doneEnabled
              ? 'bg-brand-primary text-white hover:bg-interactive-hover'
              : 'bg-grey-200 text-text-hint cursor-not-allowed',
          )}
        >
          Done
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-8 px-4 rounded-md bg-grey-100 text-sm font-semibold text-brand-primary hover:bg-blue-100"
        >
          Cancel
        </button>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onDelete}
          className="p-2 rounded hover:bg-grey-100"
          aria-label="Delete rule"
        >
          <Trash2 className="h-4 w-4 text-status-error" />
        </button>
      </div>
    </div>
  );
}
